import { CustomProxiesData } from '@/types/custom-proxies-state';
import CustomProxy from '@/types/custom-proxy';
import ProxySimulationOptionsType from '@/types/proxy-simulation-options-type';
import { QUICK_SEARCH_V2_ITEM } from '@/views/components/search/quick-search-v2/QuickSearchV2Item';
import { transformCustomProxyToQuickSearchItem } from './quick-search';
import PortfolioHolding from '@/types/portfolio-holding';
import ComparisonItem from '@/types/comparison-item';
import ProxyLogEntry from '@/types/portfolio-log-entry';

const isProxiesDataGrouped = (proxiesData: CustomProxiesData | QUICK_SEARCH_V2_ITEM[] | Record<string, QUICK_SEARCH_V2_ITEM[]>) =>
  !Array.isArray(proxiesData) && typeof proxiesData === 'object';

/**
 * Processes custom proxies data and returns both transformed items and grouping information
 * Handles both grouped (object) and flat array forms of custom proxies data
 *
 * @param proxiesData - The custom proxies data from Redux state
 * @returns Object containing transformed items and grouping flag
 */
export const processCustomProxiesData = (proxiesData: CustomProxiesData | undefined) => {
  if (!proxiesData) {
    return { items: [] as QUICK_SEARCH_V2_ITEM[], isGrouped: false };
  }

  const isGrouped = isProxiesDataGrouped(proxiesData);
  if (isGrouped) {
    const groupedItems: Record<string, QUICK_SEARCH_V2_ITEM[]> = {};

    Object.keys(proxiesData).forEach(sectionKey => {
      const sectionItems = (proxiesData as Record<string, CustomProxy[]>)[sectionKey];
      if (Array.isArray(sectionItems)) {
        const transformedItems = sectionItems.map(transformCustomProxyToQuickSearchItem);
        groupedItems[sectionKey] = transformedItems;
      }
    });

    return { items: groupedItems, isGrouped: true };
  }
  const flatItems = Array.isArray(proxiesData) ? proxiesData.map(transformCustomProxyToQuickSearchItem) : [];
  return { items: flatItems, isGrouped: false };
};

/**
 * Searches for a matching custom proxy by proxyKey
 * Handles both grouped (object) and flat array forms of custom proxies data
 *
 * @param customProxies - The custom proxies data from Redux state
 * @param proxyKey - The proxy key to search for
 * @returns The matching CustomProxy or null if not found
 */
export const findCustomProxyByKey = (customProxies: CustomProxiesData, proxyKey: string): CustomProxy | null => {
  if (!customProxies || !proxyKey) {
    return null;
  }

  const findProxyInArray = (proxies: CustomProxy[]): CustomProxy | null =>
    proxies.find((proxy: CustomProxy) => (proxy.details as ProxySimulationOptionsType)?.proxyKey === proxyKey) || null;

  const isGrouped = isProxiesDataGrouped(customProxies);
  if (isGrouped) {
    const groupedProxies = customProxies as Record<string, CustomProxy[]>;
    for (const section of Object.values(groupedProxies)) {
      if (Array.isArray(section)) {
        const matchingProxy = findProxyInArray(section);
        if (matchingProxy) {
          return matchingProxy;
        }
      }
    }
  } else {
    const flatProxies = customProxies as CustomProxy[];
    return findProxyInArray(flatProxies);
  }

  return null;
};

/**
 * Gets the total count of custom proxy items
 * Handles both grouped and flat array forms
 *
 * @param customProxies - The custom proxies data
 * @returns Total count of proxy items
 */
export const getCustomProxiesCount = (customProxies: QUICK_SEARCH_V2_ITEM[] | Record<string, QUICK_SEARCH_V2_ITEM[]>): number => {
  const isGrouped = isProxiesDataGrouped(customProxies);

  if (isGrouped) {
    const groupedItems = customProxies as Record<string, QUICK_SEARCH_V2_ITEM[]>;
    return Object.values(groupedItems).reduce((total, items) => total + items.length, 0);
  }
  const flatItems = customProxies as QUICK_SEARCH_V2_ITEM[];
  return flatItems.length;
};

/**
 * Checks if custom proxies data is empty
 * Handles both grouped (object) and flat array forms of custom proxies data
 *
 * @param customProxies - The custom proxies data to check
 * @returns True if the data is empty or undefined, false otherwise
 */
export const isCustomProxiesEmpty = (customProxies: CustomProxiesData | undefined): boolean => {
  if (!customProxies) {
    return true;
  }

  if (Array.isArray(customProxies)) {
    return customProxies.length === 0;
  }

  // For object form, check if it has any keys
  return Object.keys(customProxies).length === 0;
};

/**
 * Checks if a given portfolio holding is associated with a custom proxy.
 * Returns true if the customProxies object is not empty and a proxy matching the holding's symbol exists.
 *
 * @param customProxies - The custom proxies data from Redux state
 * @param item - The portfolio holding to check
 * @returns boolean indicating if the holding is a custom proxy
 */
export const isHoldingACustomProxy = (customProxies: CustomProxiesData, item: PortfolioHolding): boolean =>
  !isCustomProxiesEmpty(customProxies) && findCustomProxyByKey(customProxies, item.symbol!) !== null;

export const getMissingHoldingsProxyLog = (items: ComparisonItem[]): ProxyLogEntry[] | undefined => {
  const allLogs = items.flatMap(item => item.missingHoldingsProxyLog ?? []);
  return allLogs.length > 0 ? allLogs : undefined;
};
